import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, Clock } from "lucide-react";
import { api } from "../lib/api";
import { useSeo, orgJsonLd, websiteJsonLd } from "../lib/seo";
import { articleUrl, categoryUrl, formatDate } from "../lib/format";
import { ArticleCard, ArticleGrid } from "../components/articles/ArticleCard";
import { SectionHeader } from "../components/articles/SectionHeader";
import { Reveal, Stagger, StaggerItem } from "../components/common/Reveal";
import { Marquee } from "../components/common/Marquee";
import { TipOfTheDay } from "../components/common/TipOfTheDay";
import { Newsletter } from "../components/common/Newsletter";
import { AdSlot } from "../components/common/AdSlot";
import { PageLoader } from "../components/common/States";

const LINES = ["Spend Less.", "Waste Less.", "Live Better."];

const Hero = ({ featured }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="container-ab grid gap-12 pb-20 pt-10 lg:grid-cols-12 lg:gap-10 lg:pb-28 lg:pt-20" aria-labelledby="hero-heading">
      <div className="lg:col-span-6 lg:pt-8">
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="eyebrow">Everyday Tips for Smarter Living</motion.p>
        <h1 id="hero-heading" className="mt-6 font-serif text-5xl font-semibold leading-[1.02] tracking-tight sm:text-6xl lg:text-[4.6rem]" data-testid="hero-heading">
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-[0.08em]">
              <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }} className={`block ${i === 2 ? "italic text-ab-accent" : ""}`}>{line}</motion.span>
            </span>
          ))}
        </h1>
        <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6, duration: 0.7 }} className="mt-7 max-w-md text-lg leading-relaxed text-ab-text-2">
          Practical ideas for shopping, home, food, travel and everyday life.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }} className="mt-9 flex flex-wrap gap-3">
          <a href="#latest" className="btn-primary" data-testid="hero-explore-button">Explore Latest Tips <ArrowRight size={16} /></a>
          <Link to="/smart-shopping/" className="btn-secondary" data-testid="hero-smart-shopping-button">Browse Smart Shopping</Link>
        </motion.div>
      </div>

      {featured && (
        <motion.article initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }} className="group relative lg:col-span-6" data-testid="hero-featured-card">
          <Link to={articleUrl(featured)} className="block overflow-hidden rounded-3xl bg-ab-soft" data-testid="hero-featured-link">
            <div className="relative aspect-[4/3] overflow-hidden sm:aspect-[5/4]">
              <motion.img style={{ y, scale }} src={featured.featured_image} alt={featured.image_alt || featured.title} className="h-[115%] w-full object-cover" fetchPriority="high" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white sm:p-8">
                <span className="inline-flex rounded-full bg-white/15 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] backdrop-blur">Featured · {featured.category_name}</span>
                <h2 className="mt-4 font-serif text-2xl font-semibold leading-tight sm:text-3xl lg:text-[2.1rem]">{featured.title}</h2>
                <p className="mt-3 hidden max-w-lg text-sm text-white/80 sm:block">{featured.excerpt}</p>
                <div className="mt-4 flex items-center gap-4 text-xs text-white/70">
                  <span>{formatDate(featured.published_at)}</span><span className="flex items-center gap-1"><Clock size={12} /> {featured.reading_time} min read</span>
                  <span className="ml-auto flex h-10 w-10 items-center justify-center rounded-full bg-white text-ab-text transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight size={18} /></span>
                </div>
              </div>
            </div>
          </Link>
        </motion.article>
      )}
    </section>
  );
};

const CategoryBlock = ({ section, flip }) => {
  const [lead, ...rest] = section.articles;
  if (!lead) return null;
  return (
    <section className="py-16 lg:py-24" aria-labelledby={`sec-${section.category.slug}`}>
      <div className="container-ab">
        <Reveal><SectionHeader eyebrow="Section" title={section.category.name} description={section.category.description} href={categoryUrl(section.category.slug)} /></Reveal>
        <div className={`grid gap-10 lg:grid-cols-12 ${flip ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <Reveal className="lg:col-span-7"><ArticleCard article={lead} /></Reveal>
          <Stagger className="flex flex-col lg:col-span-5">
            {rest.slice(0, 4).map((a) => <StaggerItem key={a.id}><ArticleCard article={a} variant="text" /></StaggerItem>)}
          </Stagger>
        </div>
      </div>
    </section>
  );
};

export default function HomePage() {
  const [data, setData] = useState(null);
  useSeo({ title: "ArticleBlogs — Everyday Tips for Smarter Living", description: "Practical ideas for spending less, wasting less, shopping smarter and living better. Smart shopping, home, food, travel and tech tips.", path: "/", jsonLd: [orgJsonLd(), websiteJsonLd()] });
  useEffect(() => { api.get("/home").then((r) => setData(r.data)).catch(() => setData({ latest: [], sections: [] })); }, []);
  if (!data) return <PageLoader />;

  const [s1, s2, s3, s4, s5, s6, s7] = data.sections;
  return (
    <>
      <Hero featured={data.featured} />
      <Marquee />

      <section id="latest" className="container-ab py-16 lg:py-24" aria-labelledby="latest-heading">
        <Reveal><SectionHeader eyebrow="Fresh this week" title="Latest Articles" href="/everyday-tips/" linkLabel="More everyday tips" /></Reveal>
        <Stagger className="grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
          {data.latest.map((a, i) => <StaggerItem key={a.id}><ArticleCard article={a} eager={i < 3} /></StaggerItem>)}
        </Stagger>
      </section>

      {s1 && <div className="bg-ab-soft"><CategoryBlock section={s1} /></div>}
      {s2 && <CategoryBlock section={s2} flip />}

      <section className="container-ab grid gap-8 py-8 lg:grid-cols-12">
        <Reveal className="lg:col-span-7"><TipOfTheDay tip={data.tip_of_the_day} /></Reveal>
        <Reveal delay={0.1} className="lg:col-span-5">
          <div className="card-ab h-full p-7 sm:p-9">
            <p className="eyebrow">Editor's Picks</p>
            <ul className="mt-4 divide-y divide-ab-border">
              {data.editor_picks.slice(0, 4).map((a) => (
                <li key={a.id} className="py-3 first:pt-0 last:pb-0"><Link to={articleUrl(a)} className="font-serif text-base font-semibold leading-snug hover:text-ab-accent" data-testid={`editor-pick-${a.slug}`}>{a.title}</Link></li>
              ))}
            </ul>
          </div>
        </Reveal>
      </section>

      <AdSlot slot="home-mid" size="leaderboard" className="container-ab" />

      {s3 && <div className="bg-ab-soft"><CategoryBlock section={s3} /></div>}

      {s4 && (
        <section className="container-ab py-16 lg:py-24" aria-labelledby="sec-best-time-to-buy">
          <Reveal><SectionHeader eyebrow="Timing is everything" title="Best Time to Buy" description={s4.category.description} href="/best-time-to-buy/" /></Reveal>
          <Stagger className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {s4.articles.map((a) => (
              <StaggerItem key={a.id}>
                <Link to={articleUrl(a)} className="group block h-full rounded-2xl border border-ab-border bg-white p-6 transition-[border-color,transform] duration-300 hover:-translate-y-1 hover:border-ab-accent" data-testid={`btb-card-${a.slug}`}>
                  <span className="eyebrow">Best time to buy</span>
                  <h3 className="mt-3 font-serif text-xl font-semibold leading-snug">{a.title}</h3>
                  <p className="mt-3 line-clamp-3 text-sm text-ab-muted">{a.quick_answer || a.excerpt}</p>
                  <span className="btn-ghost mt-5">Read the timing <ArrowRight size={14} /></span>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </section>
      )}

      {s5 && s5.articles.length > 0 && <div className="bg-ab-soft"><CategoryBlock section={s5} flip /></div>}
      {s6 && <CategoryBlock section={s6} />}
      {s7 && <div className="bg-ab-soft"><CategoryBlock section={s7} flip /></div>}

      {data.guides.length > 0 && (
        <section className="container-ab py-16 lg:py-24" aria-labelledby="guides-heading">
          <Reveal><SectionHeader eyebrow="Evergreen" title="Popular Guides" description="Long-form references worth bookmarking." href="/guides/" /></Reveal>
          <ArticleGrid articles={data.guides} cols={2} variant="horizontal" />
        </section>
      )}

      <div className="container-ab pb-8"><Newsletter source="home" /></div>
    </>
  );
}
